import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../lib/api';
import { useToast } from '../../../hooks/useToast';
import { toLocalDatetimeInput } from '../../../utils/dateUtils';
import { SharedDataset, ShareSettings, UseShareDatasetReturn } from './types';

const defaultSettings = (name: string): ShareSettings => ({ title: name, description: '', expires_at: '', max_access_count: '' });

export function useShareDataset(tableId: string, tableName: string, isOpen: boolean): UseShareDatasetReturn {
  const [currentShares, setCurrentShares] = useState<SharedDataset[]>([]);
  const [isToggling, setIsToggling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [activeShare, setActiveShare] = useState<SharedDataset | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [shareSettings, setShareSettings] = useState<ShareSettings>(defaultSettings(tableName));
  const { showToast } = useToast();

  const loadShares = useCallback(async () => {
    try { 
      setCurrentShares(await apiClient.getDatasetShares(tableId));
    } catch (e) {
      console.error('Error loading shares:', e);
      showToast('Failed to load sharing settings', 'error');
    }
  }, [tableId, showToast]);

  useEffect(() => {
    if (isOpen) { loadShares(); setShowAdvancedOptions(false); setShareSettings(defaultSettings(tableName)); }
  }, [isOpen, loadShares, tableName]);

  useEffect(() => {
    const active = currentShares.find(s => s.is_active) || null;
    setActiveShare(active);
    setIsPublic(!!active);
    if (active) setShareSettings({
      title: active.title || tableName, description: active.description || '',
      expires_at: toLocalDatetimeInput(active.expires_at),
      max_access_count: active.max_access_count?.toString() || ''
    });
  }, [currentShares, tableName]);

  const handleTogglePublic = async () => {
    const wasPublic = isPublic;
    setIsToggling(true); setIsPublic(!wasPublic);
    try {
      if (!wasPublic) await apiClient.createShareLink(tableId, { title: shareSettings.title || tableName, description: shareSettings.description || undefined });
      else if (activeShare) await apiClient.updateSharedDataset(activeShare.share_token, { is_active: false });
      await loadShares(); setShowAdvancedOptions(false);
    } catch { setIsPublic(wasPublic); showToast('Failed to update sharing settings', 'error'); }
    finally { setIsToggling(false); }
  };

  const handleCopyShareUrl = async () => {
    if (!activeShare) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/shared/${activeShare.share_token}`);
      setCopiedToken(activeShare.share_token);
      setTimeout(() => setCopiedToken(null), 2000);
      showToast('Link copied to clipboard', 'success');
    } catch { showToast('Failed to copy link', 'error'); }
  };

  const handleSaveSettings = async () => {
    if (!activeShare) return;
    setIsSaving(true);
    try {
      await apiClient.updateSharedDataset(activeShare.share_token, {
        expires_at: shareSettings.expires_at ? new Date(shareSettings.expires_at).toISOString() : undefined,
        max_access_count: shareSettings.max_access_count ? parseInt(shareSettings.max_access_count, 10) : undefined
      });
      await loadShares(); setShowAdvancedOptions(false); showToast('Settings saved', 'success');
    } catch { showToast('Failed to update settings', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleDeleteShare = async () => {
    if (!activeShare) return;
    setIsSaving(true); setShowDeleteConfirm(false);
    try { await apiClient.deleteSharedDataset(activeShare.share_token); await loadShares(); showToast('Share link deleted', 'success'); }
    catch { showToast('Failed to delete share link', 'error'); }
    finally { setIsSaving(false); }
  };

  return {
    isPublic, isToggling, isSaving, copiedToken, activeShare,
    shareUrl: activeShare ? `${window.location.origin}/shared/${activeShare.share_token}` : '',
    shareSettings, showAdvancedOptions, showDeleteConfirm,
    setShareSettings, setShowAdvancedOptions, setShowDeleteConfirm,
    handleTogglePublic, handleCopyShareUrl, handleSaveSettings, handleDeleteShare,
  };
}
